// Type-only: this module is part of the vendored pricing-glass unit (see
// components/pricing-glass/vendor-manifest.json) and must not emit a runtime
// import of a Next internal path the consuming repo does not have.
import type { ReadonlyHeaders } from "next/dist/server/web/spec-extension/adapters/headers";

/**
 * Experiment-bucket plumbing. The Cloudflare Worker in front of the site
 * assigns each visitor one of four buckets, persists it in the
 * `iv_exp_bucket` cookie (30 days, refreshed per visit), and forwards it to
 * this origin as the `x-iv-exp-bucket` request header on home + pricing
 * routes. CloudFront keys its cache on that header alongside the
 * cloudfront-is-*-viewer device headers, so each (device × bucket) pair gets
 * its own cached copy.
 *
 * The bucket is deliberately experiment-agnostic: it is a stable
 * randomization unit, and each experiment maps buckets to its own arms
 * (e.g. a/b → control, c/d → test). Experiments running at the same time
 * share the assignment — they are not independently randomized — so run
 * bucket-keyed experiments sequentially, not overlapping. SSR reads the
 * header; the client falls back to the cookie (it is deliberately not
 * HttpOnly). Nothing renders differently per bucket yet — this only
 * surfaces the assignment to stores and analytics.
 */

export const EXPERIMENT_BUCKET_HEADER = "x-iv-exp-bucket";
export const EXPERIMENT_BUCKET_COOKIE = "iv_exp_bucket";

export const EXPERIMENT_BUCKETS = ["a", "b", "c", "d"] as const;

export type ExperimentBucket = (typeof EXPERIMENT_BUCKETS)[number];

export const parseExperimentBucket = (
  value: string | null | undefined,
): ExperimentBucket | null =>
  EXPERIMENT_BUCKETS.includes(value as ExperimentBucket)
    ? (value as ExperimentBucket)
    : null;

export const getExperimentBucket = (
  headers: ReadonlyHeaders,
): ExperimentBucket | null =>
  parseExperimentBucket(headers.get(EXPERIMENT_BUCKET_HEADER));

/**
 * Country pricing tier, also assigned by the Worker on the same routes.
 * Unlike the bucket it is deterministic (Cloudflare geo-IP ISO code → tier
 * via the worker's snapshot of iv-pro-api's CountryGroups table), so there
 * is no cookie and no client-side fallback — deriving the tier anywhere but
 * the worker would disagree with the CloudFront-cached HTML, which keys on
 * this header too. Both headers are independently optional: the worker sends
 * whichever dimensions the active experiments need, and an absent or invalid
 * header parses to null — never coerced to `row`, which is a real tier the
 * worker sends for untiered countries. The cache key composes as device
 * (always) × bucket (if sent) × group (if sent). Never key on a country
 * display name — the API's name-matching history mis-tiered ~1.55M users.
 */

export const COUNTRY_GROUP_HEADER = "x-iv-country-group";

/**
 * The visitor's ISO 3166-1 alpha-2 country, stamped by the Worker for every
 * real country (omitted for empty/XX/T1). The page cache therefore keys on it
 * per country. The API alone decides eligibility (e.g. the stripe_eu EUR
 * repricing); this value is only forwarded, and only changes what is
 * displayed.
 */
export const COUNTRY_HEADER = "x-iv-country";

const ISO_COUNTRY = /^[A-Z]{2}$/;

export const parseCountry = (
  value: string | null | undefined,
): string | null => {
  const code = value?.trim().toUpperCase();
  return code && ISO_COUNTRY.test(code) ? code : null;
};

export const getCountry = (headers: ReadonlyHeaders): string | null =>
  parseCountry(headers.get(COUNTRY_HEADER));

export const COUNTRY_GROUPS = [
  "platinum",
  "gold",
  "silver",
  "bronze",
  "row",
] as const;

export type CountryGroup = (typeof COUNTRY_GROUPS)[number];

export const parseCountryGroup = (
  value: string | null | undefined,
): CountryGroup | null =>
  COUNTRY_GROUPS.includes(value as CountryGroup)
    ? (value as CountryGroup)
    : null;

export const getCountryGroup = (
  headers: ReadonlyHeaders,
): CountryGroup | null =>
  parseCountryGroup(headers.get(COUNTRY_GROUP_HEADER)) ??
  devCountryGroupFallback();

/**
 * Local development only: the Worker that stamps the tier header is not in
 * front of `next dev`, so without it every local page renders the default
 * (v18) catalogue. `DEV_COUNTRY_GROUP=platinum` in `.env.dev.local` renders
 * that tier instead. Never consulted outside `NODE_ENV=development`, and a
 * real header always wins.
 */
const devCountryGroupFallback = (): CountryGroup | null =>
  process.env.NODE_ENV === "development"
    ? parseCountryGroup(process.env.DEV_COUNTRY_GROUP)
    : null;
